import { EntityType, Rights, calculateTimeDifferenceBetweenDates } from '@frontend/common';
import { accountIdFromResolvedEntity } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { ModalType } from '@frontend/modal';
import { TransactionPudoExtendedClient } from '@frontend/transaction/api';
import { ExtendedPudoTransaction, TransactionStateName } from '@frontend/transaction/types';
import { FormattedMessage } from 'react-intl';

import { MultiEntityButton } from '../../../components/multi-entity-button/multi-entity-button.component';
import { renderCopyableTrackingNumberCell } from '../../../components/object-list-cells/copyable-tracking-number-cell.component';
import { usePlatformModalHost } from '../../../modal/platform-modal-host/platform-modal-host.context';
import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import { ApplicationCreateButton } from '../../components/create-button';
import OverviewHeader from '../../header/overview-header';
import {
    awaitingDestinationHeaderOverwrite,
    renderCollisCountCell,
    renderDestinationSlotCell,
    renderTransactionStatusCell
} from '../awaiting-shared/awaiting-list-cells';
import { CreateTransactionModal } from '../create-transaction-modal/create-transaction-modal.component';
import { useAwaitingDropOff } from './awaiting-drop-off.controller';

export const AwaitingDropOff = () => {
    const { openPlatformDetail } = usePlatformModalHost();
    const viewProps = useAwaitingDropOff();
    return (
        <div
            key='awaiting-drop-off-transactions'
            className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'>
            <OverviewHeader
                title={
                    <FormattedMessage
                        id='feature.awaiting_drop_off'
                        description='The name of the awaiting drop off feature'
                        defaultMessage='Awaiting drop-off'
                    />
                }
                createButton={
                    <ApplicationCreateButton
                        modalId='pudo-create-transaction'
                        renderDefaultModal={() => <CreateTransactionModal formId={viewProps.formId} />}
                        modalOptions={{ type: ModalType.PANE, className: 'max-w-[600px]' }}
                        createPermissions={[{ item: EntityType.TRANSACTION, source: EntityType.ACCOUNT, right: Rights.CREATE, uid: '?' }]}
                        entity={CommonMessage.OBJECTS.TRANSACTION.DEFAULT}
                        functionality={viewProps.functionality}
                        wizardId={viewProps.functionalityWizardId}
                        requiresWizard
                        label={
                            <FormattedMessage
                                id='feature.transactions.create-transaction'
                                defaultMessage='Create transaction'
                            />
                        }
                    />
                }
            />
            <ReactiveObjectList<ExtendedPudoTransaction>
                fetch={(arg) =>
                    TransactionPudoExtendedClient.fetchPudoExtendedTransactions({
                        ...arg,
                        account_id: viewProps.currentAccount,
                        state: TransactionStateName.TRANSACTION_DROP_OFF_PENDING,
                        application: Application.PUDO,
                        without_packages: 'true',
                        without_pin_codes: 'true'
                    })
                }
                cardClassName='card bg-always-white w-full border border-border-default'
                dispatch={viewProps.dispatch}
                activeFilter={{
                    searchKeys: ['tracking_number', 'sender', 'receiver', 'purchase_order'],
                    filterKeys: ['sender_id', 'receiver_id', 'spot_id', 'slot_id', 'after', 'before'],
                    displayName: new Map([
                        ['spot_id', CommonMessage.OBJECTS.SPOT.DESTINATION],
                        ['slot_id', CommonMessage.OBJECTS.SLOT.DESTINATION],
                        ['sender_id', CommonMessage.OBJECTS.TRANSACTION.SENDERS],
                        ['receiver_id', CommonMessage.OBJECTS.TRANSACTION.RECEIVERS],
                        ['after', CommonMessage.FORMS.FROM_TIMESTAMP],
                        ['before', CommonMessage.FORMS.TO_TIMESTAMP]
                    ])
                }}
                sortingColumns={['tracking_number', 'creation_timestamp', 'waiting']}
                reverseSortingColumns={['waiting']}
                columns={{
                    columns: ['tracking_number', 'senders', 'receivers', 'waiting', 'state', 'collis', 'spot', 'slot', 'purchase_order', 'creation_timestamp'],
                    headerOverwrite: new Map([
                        ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                        ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDERS],
                        ['receivers', CommonMessage.OBJECTS.TRANSACTION.RECEIVERS],
                        ['waiting', CommonMessage.OBJECTS.TRANSACTION.WAITING_FOR],
                        ['state', awaitingDestinationHeaderOverwrite.state],
                        ['collis', awaitingDestinationHeaderOverwrite.collis],
                        ['spot', awaitingDestinationHeaderOverwrite.spot],
                        ['slot', awaitingDestinationHeaderOverwrite.slot],
                        ['purchase_order', CommonMessage.OBJECTS.TRANSACTION.PURCHASE_ORDER],
                        ['creation_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT]
                    ]),
                    customCells: new Map([
                        ['tracking_number', renderCopyableTrackingNumberCell],
                        [
                            'senders',
                            (obj) => {
                                if (obj.row.original.senders === null || obj.row.original.senders.length === 0) {
                                    return (
                                        <td className={defaultCellClassName}>
                                            <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                        </td>
                                    );
                                }
                                return (
                                    <td
                                        key={obj.row.original.senders[0].entity_id}
                                        className={defaultCellClassName}>
                                        <div className='flex gap-2.5 truncate text-sm font-medium'>
                                            <PlatformObjectBadge
                                                id={obj.row.original.senders[0].entity_id}
                                                entityType={EntityType.USER}
                                                display={obj.row.original.senders[0].name}
                                                key={obj.row.original.senders[0].entity_id}
                                                foreign={obj.row.original.senders[0].foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.senders[0])}
                                            />
                                            {obj.row.original.senders.length > 1 && (
                                                <MultiEntityButton
                                                    entities={obj.row.original.senders.slice(1)}
                                                    render={(entity) => (
                                                        <PlatformObjectBadge
                                                            id={entity.entity_id}
                                                            entityType={EntityType.USER}
                                                            display={entity.name}
                                                            key={entity.entity_id}
                                                            foreign={entity.foreign}
                                                            accountId={accountIdFromResolvedEntity(entity)}
                                                        />
                                                    )}
                                                />
                                            )}
                                        </div>
                                    </td>
                                );
                            }
                        ],
                        [
                            'receivers',
                            (obj) => {
                                if (obj.row.original.receivers === null || obj.row.original.receivers.length === 0) {
                                    return (
                                        <td className={defaultCellClassName}>
                                            <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                        </td>
                                    );
                                }
                                return (
                                    <td
                                        key={obj.row.original.receivers[0].entity_id}
                                        className={defaultCellClassName}>
                                        <div className='flex gap-2.5 truncate text-sm font-medium'>
                                            <PlatformObjectBadge
                                                id={obj.row.original.receivers[0].entity_id}
                                                entityType={EntityType.USER}
                                                display={obj.row.original.receivers[0].name}
                                                key={obj.row.original.receivers[0].entity_id}
                                                foreign={obj.row.original.receivers[0].foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.receivers[0])}
                                            />
                                            {obj.row.original.receivers.length > 1 && (
                                                <MultiEntityButton
                                                    entities={obj.row.original.receivers.slice(1)}
                                                    render={(entity) => (
                                                        <PlatformObjectBadge
                                                            id={entity.entity_id}
                                                            entityType={EntityType.USER}
                                                            display={entity.name}
                                                            key={entity.entity_id}
                                                            foreign={entity.foreign}
                                                            accountId={accountIdFromResolvedEntity(entity)}
                                                        />
                                                    )}
                                                />
                                            )}
                                        </div>
                                    </td>
                                );
                            }
                        ],
                        [
                            'waiting',
                            (obj) => {
                                return (
                                    <td
                                        key={obj.row.original.update_timestamp}
                                        className={defaultCellClassName}>
                                        {calculateTimeDifferenceBetweenDates(obj.row.original.update_timestamp, new Date(Date.now()).toISOString())}
                                    </td>
                                );
                            }
                        ],
                        ['state', renderTransactionStatusCell],
                        ['collis', renderCollisCountCell],
                        [
                            'spot',
                            (obj) => {
                                return (
                                    <td
                                        key={obj.row.original.spot?.entity_id}
                                        className={defaultCellClassName}>
                                        <PlatformObjectBadge
                                            id={obj.row.original.spot?.entity_id}
                                            display={obj.row.original.spot?.name}
                                            entityType={EntityType.SPOT}
                                            key={obj.row.original.spot?.entity_id}
                                            foreign={obj.row.original.spot?.foreign}
                                            accountId={accountIdFromResolvedEntity(obj.row.original.spot)}
                                        />
                                    </td>
                                );
                            }
                        ],
                        ['slot', renderDestinationSlotCell]
                    ])
                }}
                type={EntityType.TRANSACTION}
                eventListener={EntityType.TRANSACTION}
                showCount
                id='awaiting-drop-off-list'
                useStaticListId
                onSelectElement={(obj) => openPlatformDetail({ entityType: EntityType.TRANSACTION, id: obj.id, context: { application: Application.PUDO } })}
            />
        </div>
    );
};
